import type { AtualizarPreferenciasDeVozDto, PreferenciasDeVozDto } from '@rimati/shared';
import type { ContactSort, WhatsAppMessage, WhatsAppReminderDto } from '@rimati/shared';
import type {
  ReceiptDto,
  ReceiptIssueResultDto,
  PublicQuoteDto,
  QuoteDecisionInput,
  QuoteLinkDto,
  QuoteLinkIssueResultDto,
  ReceiptIssuerDto,
  ReceiptSettingsDto,
  ReceiptSessionsPreviewDto,
  ReceiptSourceKind,
  ReceiptVariant,
} from '@rimati/shared';
import type { MediaGovernanceDto } from '@rimati/shared';
import type { DataGovernancePolicyDto, UpdateDataGovernancePolicyRequest } from '@rimati/shared';
import type { SecurityReadinessReport } from '@rimati/shared';
import type { ExternalReadinessReportDto } from '@rimati/shared';
import type { ScreenContextDto } from '@rimati/shared';
import type {
  IncidenteDto,
  SugestoesDto,
  TicketDto,
  TicketDaFilaDto,
  VisaoDoOperadorDto,
  AtivacaoFunilDto,
  OnboardingDto,
  AdminChatReplyDto,
  AdminChatSessionDto,
  AdminChatViewDto,
  AdminContextKind,
  AdminPeriod,
  AdminTaskDto,
  PriorityFeedDto,
  AgentProposalDto,
  DecideProposalRequest,
  SetupMessageDto,
  SetupSessionDto,
  AutomationResultDto,
  AgentTodayDto,
  CalendarSummaryDto,
  NoShowsByClientDto,
  ReceptionEntryDto,
  ActivityDto,
  AgentDto,
  AnalyticsPeriod,
  AppointmentDto,
  TelehealthConfigurationDto,
  CalendarConnectionDto,
  ExternalCalendarEventDto,
  CalendarExportEventDto,
  IntegrationHomologationDto,
  IntegrationHomologationKind,
  IntegrationHomologationReportDto,
  UpdateIntegrationHomologationRequest,
  ProfessionalPayoutRuleDto,
  ProfessionalPayoutAccrualDto,
  ClinicalDayReviewDto,
  ClinicalDayClosingDto,
  CreateAppointmentRequest,
  CreateAppointmentSeriesRequest,
  AppointmentSeriesDto,
  AppointmentSeriesPreviewDto,
  AppointmentSeriesChangePreviewDto,
  AppointmentSeriesShiftPreviewDto,
  AppointmentSeriesShiftResultDto,
  CreateWaitlistEntryRequest,
  WaitlistEntryDto,
  AvailabilityRuleDto,
  ClinicServiceDto,
  ClinicalResourceDto,
  ClinicalResourceImpactDto,
  PatientProfileDto,
  PatientPortalGrantDto,
  PatientPortalSessionDto,
  PatientPortalScope,
  PatientRelationshipDto,
  PatientDataRequestDto,
  PatientDataRequestType,
  PatientPhoneChangeDto,
  PatientPreVisitFormDto,
  PatientPreVisitResponses,
  PatientSatisfactionSurveyDto,
  PatientSatisfactionSummaryDto,
  ClinicalCaptureDto,
  ClinicalAccessLogDto,
  ClinicalRecordDto,
  ClinicalRecordType,
  MealPlanDto,
  MealPlanShareResultDto,
  PublicMealPlanDto,
  ExamWorkflowDto,
  UpdatePatientProfileRequest,
  HealthBillingWorkspaceDto,
  HealthPayerDto,
  HealthPlanDto,
  HealthClaimDto,
  CarePackageAvailabilityDto,
  CarePackageDto,
  JustifiedAbsenceResultDto,
  ReplacementPolicyDto,
  CarePackageWorkspaceDto,
  ClinicalFinanceEventDto,
  ClinicalFinanceWorkspaceDto,
  ClinicalFinanceExportDto,
  ClinicalFinanceFilters,
  ClinicalFinanceSavedViewDto,
  ClinicalFinanceChartType,
  ClinicalFinanceViewColumn,
  ClinicalFinanceReportDto,
  ClinicalFinanceReportFileDto,
  ClinicalFinanceReportFormat,
  ClinicalFinanceExportJobDto,
  ClinicalFinanceReportScheduleDto,
  UserRole,
  ClinicalMaterialWorkspaceDto,
  ClinicalMaterialLotDto,
  ClinicalMaterialConsumptionDto,
  ClinicalMaterialPurchaseDto,
  ClinicalMaterialReplenishmentRequestDto,
  ClinicalMaterialSeparationResultDto,
  AutomationDto,
  AutomationTemplate,
  AvailabilitySlotDto,
  DashboardDto,
  CapabilityHealthReportDto,
  DiagnosticsDto,
  FollowUpDto,
  InsightsDto,
  MediaAnalyticsDto,
  MediaAnalyticsExportDto,
  MediaAnalyticsKind,
  MediaAnalyticsState,
  KnowledgeItemDto,
  AuthSessionDto,
  ChangePasswordRequest,
  ChannelDto,
  CompanyDto,
  ContactDto,
  CreateUserRequest,
  CreateUserResultDto,
  ConversationDto,
  HandoffContextDto,
  MeDto,
  MessageDto,
  OpportunityDto,
  Paginated,
  PipelineDto,
  ProfessionalDto,
  PlaygroundResultDto,
  RunPlaygroundRequest,
  SimulateMessageResult,
  ToolName,
  UpdateAgentRequest,
  UpdateCompanyRequest,
  UpdateContactRequest,
  UpdateUserRequest,
  UserDto,
} from '@rimati/shared';
import { API_BASE, getAccessToken, request, requestBlob } from './client.js';

/** Um lugar por recurso. Nenhum componente monta URL na mao. */

export const authApi = {
  login: (email: string, password: string) =>
    request<AuthSessionDto>('/auth/login', {
      method: 'POST',
      body: { email, password },
      skipAuthRedirect: true,
    }),
  refresh: () =>
    request<AuthSessionDto>('/auth/refresh', { method: 'POST', skipAuthRedirect: true }),
  me: () => request<MeDto>('/auth/me'),
  logout: () => request<{ loggedOut: boolean }>('/auth/logout', { method: 'POST' }),
};

/** ML174b - ficha de medidas generica do cliente (rotulo livre, valor, unidade). */
export interface ContactMeasurementEntry {
  label: string;
  value: number;
  unit: string;
}
export interface ContactMeasurementSheet {
  id: string;
  contactId: string;
  measuredOn: string;
  entries: ContactMeasurementEntry[];
  note: string;
  recordedBy: { id: string; name: string };
  recordedAt: string;
  current: boolean;
}
export interface ContactMeasurementList {
  items: ContactMeasurementSheet[];
  /** A empresa ativou uma ferramenta que usa medidas, ou o cliente ja tem ficha. */
  available: boolean;
  suggestions: { labels: string[]; units: string[] };
}

export const contactsApi = {
  birthdays: (days = 7) =>
    request<{
      from: string;
      days: number;
      items: Array<WhatsAppReminderDto & { daysUntil: number }>;
      withoutLink: number;
      optedOut: number;
    }>('/contacts/birthdays', { query: { days } }),
  /** L2 (d): quem está sumido há X dias, com mensagem "hora de voltar". */
  comeBack: (days?: number) =>
    request<{
      today: string;
      days: number;
      items: Array<
        WhatsAppMessage & {
          id: string;
          customerName: string;
          lastDate: string;
          daysAway: number;
          source: 'atendimento' | 'compra';
        }
      >;
      total: number;
      withoutLink: number;
      optedOut: number;
    }>('/contacts/come-back', { query: days ? { days } : {} }),
  setComeBackDays: (days: number) =>
    request<{ days: number }>('/contacts/come-back/settings', { method: 'PUT', body: { days } }),
  markBirthdayOpened: (id: string) =>
    request<{ id: string; openedAt: string }>(`/contacts/${id}/birthday-reminder-opened`, {
      method: 'POST',
      body: {},
    }),
  measurements: (id: string) => request<ContactMeasurementList>(`/contacts/${id}/measurements`),
  purchases: (id: string) =>
    request<{
      contactId: string;
      count: number;
      totalSpentCents: number;
      unavailable: ('vendas' | 'encomendas')[];
      items: {
        kind: 'sale' | 'order';
        id: string;
        number: string | null;
        date: string;
        lines: { name: string; quantity: number; totalCents: number }[];
        totalCents: number;
        returnedCents: number;
        netCents: number;
        status: string;
      }[];
    }>(`/contacts/${id}/purchases`),
  addMeasurement: (
    id: string,
    body: { id: string; entries: ContactMeasurementEntry[]; note: string; measuredOn?: string },
  ) => request<ContactMeasurementSheet>(`/contacts/${id}/measurements`, { method: 'POST', body }),
  list: (
    query: {
      search?: string;
      status?: string;
      sort?: ContactSort;
      limit?: number;
      page?: number;
    } = {},
  ) => request<Paginated<ContactDto>>('/contacts', { query }),
  get: (id: string) => request<ContactDto>(`/contacts/${id}`),
  update: (id: string, body: UpdateContactRequest) =>
    request<ContactDto>(`/contacts/${id}`, { method: 'PATCH', body }),
  mergePreview: (sourceId: string, targetId: string) =>
    request<ContactMergePreview>(`/contacts/${sourceId}/merge/preview`, {
      method: 'POST',
      body: { targetId },
    }),
  merge: (
    sourceId: string,
    body: {
      targetId: string;
      digest: string;
      confirmed: true;
      resolution: Record<string, 'source' | 'target'>;
    },
  ) => request<ContactMergeResult>(`/contacts/${sourceId}/merge`, { method: 'POST', body }),
  optOut: (id: string, body: { scope: 'acompanhamentos' | 'tudo'; source: 'cliente' | 'equipe' }) =>
    request<ContactDto>(`/contacts/${id}/descadastro`, { method: 'PUT', body }),
  reactivate: (id: string, reason: string) =>
    request<{ ok: true }>(`/contacts/${id}/descadastro`, { method: 'DELETE', body: { reason } }),
};

export const patientsApi = {
  accessLog: (limit = 50) =>
    request<ClinicalAccessLogDto[]>('/patients/access-log', { query: { limit } }),
  get: (contactId: string, purpose: 'scheduling' | 'care' | 'billing' | 'support' = 'care') =>
    request<PatientProfileDto | null>(`/patients/${contactId}`, { query: { purpose } }),
  update: (contactId: string, body: UpdatePatientProfileRequest) =>
    request<PatientProfileDto>(`/patients/${contactId}`, { method: 'PUT', body }),
  revokeConsent: (contactId: string, purpose: 'care' | 'support' = 'care') =>
    request<PatientProfileDto>(`/patients/${contactId}/consent/revoke`, {
      method: 'POST',
      body: { purpose, confirmed: true },
    }),
  setRepresentationReview: (
    contactId: string,
    body: { status: 'none' | 'pending' | 'restricted'; reason: string; confirmed: true },
  ) =>
    request<PatientProfileDto>(`/patients/${contactId}/representation-review`, {
      method: 'POST',
      body,
    }),
  phoneChanges: (contactId: string) =>
    request<PatientPhoneChangeDto[]>(`/patients/${contactId}/phone-changes`),
  requestPhoneChange: (contactId: string, phone: string, reason: string) =>
    request<PatientPhoneChangeDto>(`/patients/${contactId}/phone-changes`, {
      method: 'POST',
      body: { phone, reason, confirmed: true },
    }),
  verifyPhoneChange: (contactId: string, changeId: string, method: string) =>
    request<PatientPhoneChangeDto>(`/patients/${contactId}/phone-changes/${changeId}/verify`, {
      method: 'POST',
      body: { method, confirmed: true },
    }),
  applyPhoneChange: (contactId: string, changeId: string) =>
    request<PatientPhoneChangeDto>(`/patients/${contactId}/phone-changes/${changeId}/apply`, {
      method: 'POST',
      body: { confirmed: true },
    }),
  cancelPhoneChange: (contactId: string, changeId: string) =>
    request<PatientPhoneChangeDto>(`/patients/${contactId}/phone-changes/${changeId}/cancel`, {
      method: 'POST',
      body: { confirmed: true },
    }),
  preVisitForms: (contactId: string) =>
    request<PatientPreVisitFormDto[]>(`/patients/${contactId}/pre-visit-forms`),
  submitPreVisitForm: (
    contactId: string,
    appointmentId: string,
    responses: PatientPreVisitResponses,
    correctionReason?: string | null,
  ) =>
    request<PatientPreVisitFormDto>(`/patients/${contactId}/pre-visit-forms`, {
      method: 'POST',
      body: { appointmentId, responses, correctionReason, confirmed: true },
    }),
  reviewPreVisitForm: (contactId: string, formId: string, note: string) =>
    request<PatientPreVisitFormDto>(`/patients/${contactId}/pre-visit-forms/${formId}/review`, {
      method: 'POST',
      body: { note, confirmed: true },
    }),
  satisfactionSurveys: (contactId: string) =>
    request<PatientSatisfactionSurveyDto[]>(`/patients/${contactId}/satisfaction-surveys`),
  issueSatisfactionSurvey: (contactId: string, appointmentId: string, expiresDays = 7) =>
    request<{ survey: PatientSatisfactionSurveyDto; token: string }>(
      `/patients/${contactId}/satisfaction-surveys`,
      { method: 'POST', body: { appointmentId, expiresDays, confirmed: true } },
    ),
  satisfactionSummary: (from: string, to: string) =>
    request<PatientSatisfactionSummaryDto>('/patients/quality/satisfaction-summary', {
      query: { from, to },
    }),
  updateSatisfactionComplaint: (surveyId: string, action: 'resolve' | 'reopen', note: string) =>
    request<PatientSatisfactionSurveyDto>(`/patients/quality/satisfaction/${surveyId}/complaint`, {
      method: 'POST',
      body: { action, note, confirmed: true },
    }),
  clinicalCaptures: (contactId: string) =>
    request<ClinicalCaptureDto[]>(`/patients/${contactId}/clinical-captures`),
  recordClinicalCaptureDecision: (
    contactId: string,
    body: {
      appointmentId: string;
      decision: 'consented' | 'declined';
      purpose: string;
      recipients: string;
      retentionUntil?: string | null;
      alternativeOffered: string;
    },
  ) =>
    request<ClinicalCaptureDto>(`/patients/${contactId}/clinical-captures`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  addClinicalCaptureDraft: (
    contactId: string,
    captureId: string,
    body: {
      source: 'ai_transcription' | 'manual';
      content: string;
      modelName?: string | null;
      modelVersion?: string | null;
      uncertainSegments: string[];
      negationSegments: string[];
      negationsChecked: boolean;
      missingInformation: string[];
    },
  ) =>
    request<ClinicalCaptureDto>(`/patients/${contactId}/clinical-captures/${captureId}/drafts`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  recordClinicalCaptureFailure: (contactId: string, captureId: string, message: string) =>
    request<ClinicalCaptureDto>(`/patients/${contactId}/clinical-captures/${captureId}/failure`, {
      method: 'POST',
      body: { message, confirmed: true },
    }),
  reviewClinicalCaptureDraft: (
    contactId: string,
    captureId: string,
    version: number,
    reviewedContent: string,
    note: string,
  ) =>
    request<ClinicalCaptureDto>(`/patients/${contactId}/clinical-captures/${captureId}/review`, {
      method: 'POST',
      body: { version, reviewedContent, note, confirmed: true },
    }),
  relationships: (contactId: string) =>
    request<PatientRelationshipDto[]>(`/patients/${contactId}/relationships`),
  createRelationship: (
    contactId: string,
    body: {
      relatedContactId: string;
      type: 'legal_guardian' | 'payer' | 'contact' | 'emergency';
      legalAuthority: boolean;
      canReceiveAdministrativeMessages: boolean;
      verificationMethod?: string | null;
      validUntil?: string | null;
      confirmed: true;
    },
  ) =>
    request<{ id: string; relatedContactName: string }>(`/patients/${contactId}/relationships`, {
      method: 'POST',
      body,
    }),
  deactivateRelationship: (contactId: string, relationshipId: string) =>
    request<{ id: string; isActive: false }>(
      `/patients/${contactId}/relationships/${relationshipId}/deactivate`,
      { method: 'POST', body: { confirmed: true } },
    ),
  issuePortalGrant: (
    contactId: string,
    body: {
      subjectContactId: string;
      scopes: PatientPortalScope[];
      expiresHours: number;
      confirmed: true;
    },
  ) =>
    request<PatientPortalGrantDto>(`/patients/${contactId}/portal-grants`, {
      method: 'POST',
      body,
    }),
  dataRequests: (contactId: string) =>
    request<PatientDataRequestDto[]>(`/patients/${contactId}/data-requests`),
  createDataRequest: (
    contactId: string,
    body: { type: PatientDataRequestType; requestDetails: string },
  ) =>
    request<PatientDataRequestDto>(`/patients/${contactId}/data-requests`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  verifyDataRequestIdentity: (contactId: string, requestId: string, method: string) =>
    request<PatientDataRequestDto>(
      `/patients/${contactId}/data-requests/${requestId}/verify-identity`,
      { method: 'POST', body: { method, confirmed: true } },
    ),
  resolveDataRequest: (
    contactId: string,
    requestId: string,
    outcome: 'fulfilled' | 'rejected' | 'retained',
    resolution: string,
  ) =>
    request<PatientDataRequestDto>(`/patients/${contactId}/data-requests/${requestId}/resolve`, {
      method: 'POST',
      body: { outcome, resolution, confirmed: true },
    }),
};

export const patientPortalApi = {
  session: (token: string, birthDate: string) =>
    request<PatientPortalSessionDto>('/portal-paciente/session', {
      method: 'POST',
      body: { token, birthDate },
      skipAuthRedirect: true,
    }),
  cancelAppointment: (token: string, birthDate: string, appointmentId: string, reason: string) =>
    request<{ appointmentId: string; cancelled: true }>(
      `/portal-paciente/appointments/${appointmentId}/cancel`,
      {
        method: 'POST',
        body: { token, birthDate, reason, confirmed: true },
        skipAuthRedirect: true,
      },
    ),
  submitPreVisitForm: (
    token: string,
    birthDate: string,
    appointmentId: string,
    responses: PatientPreVisitResponses,
    correctionReason?: string | null,
  ) =>
    request<PatientPreVisitFormDto>(
      `/portal-paciente/appointments/${appointmentId}/pre-visit-form`,
      {
        method: 'POST',
        body: { token, birthDate, responses, correctionReason, confirmed: true },
        skipAuthRedirect: true,
      },
    ),
};

export const satisfactionSurveyApi = {
  respond: (token: string, score: number, comment: string | null, optOutFuture: boolean) =>
    request<{ received: true; optedOutFuture: boolean }>('/portal-paciente/satisfaction/respond', {
      method: 'POST',
      body: { token, score, comment, optOutFuture, confirmed: true },
      skipAuthRedirect: true,
    }),
};

export const clinicalRecordsApi = {
  list: (contactId: string) =>
    request<ClinicalRecordDto[]>(`/clinical-records/${contactId}/records`),
  create: (
    contactId: string,
    body: {
      type: ClinicalRecordType;
      title: string;
      content?: string;
      mealPlan?: MealPlanDto;
      appointmentId?: string | null;
    },
  ) =>
    request<ClinicalRecordDto>(`/clinical-records/${contactId}/records`, {
      method: 'POST',
      body,
    }),
  reviseDraft: (contactId: string, recordId: string, content: string) =>
    request<ClinicalRecordDto>(`/clinical-records/${contactId}/records/${recordId}/revisions`, {
      method: 'POST',
      body: { content },
    }),
  /** Plano alimentar: anexo (PDF/imagem), link do paciente e revogação. */
  attachFile: (
    contactId: string,
    recordId: string,
    body: { fileName: string; mimeType: string; contentBase64: string },
  ) =>
    request<{ id: string }>(`/clinical-records/${contactId}/records/${recordId}/attachment`, {
      method: 'POST',
      body,
    }),
  readFile: (contactId: string, recordId: string) =>
    request<{ fileName: string; mimeType: string; base64: string }>(
      `/clinical-records/${contactId}/records/${recordId}/attachment`,
    ),
  share: (contactId: string, recordId: string, expiresInDays?: number) =>
    request<MealPlanShareResultDto>(`/clinical-records/${contactId}/records/${recordId}/share`, {
      method: 'POST',
      body: expiresInDays ? { expiresInDays } : {},
    }),
  revokeShare: (contactId: string, recordId: string) =>
    request<{ revoked: boolean }>(`/clinical-records/${contactId}/records/${recordId}/share/revoke`, {
      method: 'POST',
      body: {},
    }),
  publicMealPlan: (token: string) =>
    request<PublicMealPlanDto>(`/planos-alimentares-publicos/${encodeURIComponent(token)}`, {
      anonymous: true,
    }),
  publicMealPlanFileUrl: (token: string) =>
    `${API_BASE}/planos-alimentares-publicos/${encodeURIComponent(token)}/anexo`,
  finalize: (contactId: string, recordId: string, professionalId: string) =>
    request<ClinicalRecordDto>(`/clinical-records/${contactId}/records/${recordId}/finalize`, {
      method: 'POST',
      body: { professionalId, confirmed: true },
    }),
  amend: (
    contactId: string,
    recordId: string,
    body: { content: string; reason: string; professionalId: string },
  ) =>
    request<ClinicalRecordDto>(`/clinical-records/${contactId}/records/${recordId}/amendments`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
};

export const examWorkflowsApi = {
  list: (contactId: string) => request<ExamWorkflowDto[]>(`/clinical-records/${contactId}/exams`),
  create: (contactId: string, examName: string, orderRecordId: string) =>
    request<ExamWorkflowDto>(`/clinical-records/${contactId}/exams`, {
      method: 'POST',
      body: { examName, orderRecordId },
    }),
  performed: (contactId: string, examId: string) =>
    request<ExamWorkflowDto>(`/clinical-records/${contactId}/exams/${examId}/performed`, {
      method: 'POST',
      body: { confirmed: true },
    }),
  linkReport: (contactId: string, examId: string, reportRecordId: string) =>
    request<ExamWorkflowDto>(`/clinical-records/${contactId}/exams/${examId}/report`, {
      method: 'POST',
      body: { reportRecordId },
    }),
  review: (contactId: string, examId: string) =>
    request<ExamWorkflowDto>(`/clinical-records/${contactId}/exams/${examId}/review`, {
      method: 'POST',
      body: { confirmed: true },
    }),
  release: (contactId: string, examId: string) =>
    request<ExamWorkflowDto>(`/clinical-records/${contactId}/exams/${examId}/release`, {
      method: 'POST',
      body: { confirmed: true },
    }),
  scheduleNotification: (contactId: string, examId: string) =>
    request<ExamWorkflowDto>(`/clinical-records/${contactId}/exams/${examId}/notification`, {
      method: 'POST',
      body: { confirmed: true },
    }),
  prepareRetification: (
    contactId: string,
    examId: string,
    reportRecordId: string,
    reason: string,
  ) =>
    request<ExamWorkflowDto>(`/clinical-records/${contactId}/exams/${examId}/retification`, {
      method: 'POST',
      body: { reportRecordId, reason, confirmed: true },
    }),
};

export const healthBillingApi = {
  workspace: () => request<HealthBillingWorkspaceDto>('/health-billing'),
  createPayerPlanSetup: (body: {
    operationId: string;
    payer: {
      name: string;
      type: 'insurance' | 'self_pay' | 'company';
      registrationCode?: string | null;
      contactChannel?: string | null;
      submissionDays?: number | null;
      paymentDays?: number | null;
    };
    plan: {
      name: string;
      contractCode?: string | null;
      coveredServiceCodes?: string[];
      authorizationRequiredServiceCodes?: string[];
      procedureMappings?: Array<{
        serviceCode: string;
        tussCode: string;
        tussVersion: string;
        tissVersion: string;
      }>;
      notes?: string | null;
    };
  }) =>
    request<{ payer: HealthPayerDto; plan: HealthPlanDto }>('/health-billing/payer-plan-setups', {
      method: 'POST',
      body,
    }),
  createPayer: (body: {
    name: string;
    type: 'insurance' | 'self_pay' | 'company';
    registrationCode?: string | null;
    contactChannel?: string | null;
    submissionDays?: number | null;
    paymentDays?: number | null;
  }) => request<HealthPayerDto>('/health-billing/payers', { method: 'POST', body }),
  createPlan: (body: {
    payerId: string;
    name: string;
    contractCode?: string | null;
    coveredServiceCodes?: string[];
    authorizationRequiredServiceCodes?: string[];
    procedureMappings?: Array<{
      serviceCode: string;
      tussCode: string;
      tussVersion: string;
      tissVersion: string;
    }>;
    notes?: string | null;
  }) => request<HealthPlanDto>('/health-billing/plans', { method: 'POST', body }),
  updatePlan: (
    id: string,
    body: {
      name: string;
      contractCode?: string | null;
      coveredServiceCodes?: string[];
      authorizationRequiredServiceCodes?: string[];
      procedureMappings?: Array<{
        serviceCode: string;
        tussCode: string;
        tussVersion: string;
        tissVersion: string;
      }>;
      notes?: string | null;
      isActive?: boolean;
    },
  ) =>
    request<HealthPlanDto>(`/health-billing/plans/${id}`, {
      method: 'PUT',
      body: { ...body, confirmed: true },
    }),
  createCoverage: (body: {
    operationId?: string;
    contactId: string;
    payerId: string;
    planId: string;
    memberNumber: string;
    validFrom?: string | null;
    validUntil?: string | null;
  }) =>
    request<{ id: string; contactName: string }>('/health-billing/coverages', {
      method: 'POST',
      body,
    }),
  eligibility: (
    id: string,
    body: {
      status: 'eligible' | 'ineligible' | 'unknown';
      protocol?: string | null;
      note?: string | null;
      validFrom?: string | null;
      validUntil?: string | null;
      confirmed: true;
    },
  ) =>
    request<{ id: string; status: string; checkedAt: string }>(
      `/health-billing/coverages/${id}/eligibility`,
      { method: 'POST', body },
    ),
  createAuthorization: (body: {
    operationId?: string;
    coverageId: string;
    serviceCode: string;
    serviceName: string;
    requestedQuantity: number;
    validFrom?: string | null;
    validUntil?: string | null;
    note?: string | null;
  }) =>
    request<{ id: string; status: string }>('/health-billing/authorizations', {
      method: 'POST',
      body,
    }),
  decideAuthorization: (
    id: string,
    body: {
      status: 'authorized' | 'partially_authorized' | 'denied';
      authorizedQuantity: number;
      authorizationCode?: string | null;
      validFrom?: string | null;
      validUntil?: string | null;
      note?: string | null;
      confirmed: true;
    },
  ) =>
    request<{ id: string; status: string }>(`/health-billing/authorizations/${id}/decision`, {
      method: 'POST',
      body,
    }),
  createClaim: (body: {
    operationId: string;
    payerId: string;
    planId?: string | null;
    guideNumber?: string | null;
    batchNumber?: string | null;
    tissVersion?: string | null;
    note?: string | null;
    items: Array<{
      key: string;
      appointmentId?: string | null;
      authorizationId?: string | null;
      serviceCode: string;
      tussCode?: string | null;
      tussVersion?: string | null;
      description: string;
      quantity: number;
      billedCents: number;
    }>;
  }) => request<HealthClaimDto>('/health-billing/claims', { method: 'POST', body }),
  cancelClaim: (
    id: string,
    body: { operationId: string; reason: string; externalReference?: string | null },
  ) =>
    request<HealthClaimDto>(`/health-billing/claims/${id}/cancel`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  reconcileClaim: (
    id: string,
    body: {
      operationId: string;
      status: 'submitted' | 'partially_paid' | 'paid' | 'denied' | 'technical_rejection';
      receivedAt?: string | null;
      submittedAt?: string | null;
      confirmed: true;
      items: Array<{
        key: string;
        approvedCents: number;
        paidCents: number;
        deniedCents: number;
        denialReason?: string | null;
        appealStatus?: 'none' | 'draft' | 'submitted' | 'accepted' | 'rejected';
        appealNote?: string | null;
        appealOwnerUserId?: string | null;
        appealDueAt?: string | null;
      }>;
    },
  ) => request<HealthClaimDto>(`/health-billing/claims/${id}/reconcile`, { method: 'POST', body }),
  recordSubmission: (
    id: string,
    body: {
      operationId: string;
      status: 'accepted' | 'technical_rejection' | 'pending';
      protocol?: string | null;
      errorMessage?: string | null;
      confirmed: true;
    },
  ) =>
    request<HealthClaimDto>(`/health-billing/claims/${id}/submissions`, { method: 'POST', body }),
};

export const clinicalFinanceApi = {
  workspace: (filters?: ClinicalFinanceFilters, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.paymentMethod) params.set('paymentMethod', filters.paymentMethod);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.sort) params.set('sort', filters.sort);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
    if (filters?.targetCents) params.set('targetCents', String(filters.targetCents));
    const query = params.toString();
    return request<ClinicalFinanceWorkspaceDto>(`/clinical-finance${query ? `?${query}` : ''}`, {
      signal,
    });
  },
  exportCsv: (filters?: ClinicalFinanceFilters) => {
    const params = new URLSearchParams();
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.paymentMethod) params.set('paymentMethod', filters.paymentMethod);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.sort) params.set('sort', filters.sort);
    if (filters?.targetCents) params.set('targetCents', String(filters.targetCents));
    const query = params.toString();
    return request<ClinicalFinanceExportDto>(`/clinical-finance/export${query ? `?${query}` : ''}`);
  },
  recordEvent: (body: {
    operationId: string;
    kind: 'insurance_receipt';
    date: string;
    sourceId: string;
    accountId: string;
    description: string;
    evidenceReference: string;
    confirmed: true;
  }) => request<ClinicalFinanceEventDto>('/clinical-finance/events', { method: 'POST', body }),
  views: () => request<ClinicalFinanceSavedViewDto[]>('/clinical-finance/views'),
  createView: (body: {
    name: string;
    filters: ClinicalFinanceFilters;
    chartType: ClinicalFinanceChartType;
    columns: ClinicalFinanceViewColumn[];
    sharedRoles: UserRole[];
  }) => request<ClinicalFinanceSavedViewDto>('/clinical-finance/views', { method: 'POST', body }),
  updateView: (
    id: string,
    body: {
      revision: number;
      name: string;
      filters: ClinicalFinanceFilters;
      chartType: ClinicalFinanceChartType;
      columns: ClinicalFinanceViewColumn[];
      sharedRoles: UserRole[];
    },
  ) =>
    request<ClinicalFinanceSavedViewDto>(`/clinical-finance/views/${id}`, {
      method: 'PUT',
      body,
    }),
  removeView: (id: string) =>
    request<{ removed: true }>(`/clinical-finance/views/${id}`, { method: 'DELETE' }),
  reports: () => request<ClinicalFinanceReportDto[]>('/clinical-finance/reports'),
  createReport: (body: { name: string; filters: ClinicalFinanceFilters }) =>
    request<ClinicalFinanceReportDto>('/clinical-finance/reports', { method: 'POST', body }),
  reviseReport: (id: string) =>
    request<ClinicalFinanceReportDto>(`/clinical-finance/reports/${id}/revisions`, {
      method: 'POST',
    }),
  reportFile: (id: string, format: ClinicalFinanceReportFormat) =>
    request<ClinicalFinanceReportFileDto>(`/clinical-finance/reports/${id}/files/${format}`),
  exportJobs: () => request<ClinicalFinanceExportJobDto[]>('/clinical-finance/export-jobs'),
  requestExportJob: (body: {
    reportId: string;
    format: ClinicalFinanceReportFormat;
    estimatedRows: number;
  }) =>
    request<ClinicalFinanceExportJobDto>('/clinical-finance/export-jobs', {
      method: 'POST',
      body,
    }),
  cancelExportJob: (id: string) =>
    request<ClinicalFinanceExportJobDto>(`/clinical-finance/export-jobs/${id}/cancel`, {
      method: 'POST',
    }),
  exportJobFile: (id: string) =>
    request<ClinicalFinanceReportFileDto>(`/clinical-finance/export-jobs/${id}/file`),
  reportSchedules: () =>
    request<ClinicalFinanceReportScheduleDto[]>('/clinical-finance/report-schedules'),
  createReportSchedule: (body: {
    reportId: string;
    localTime: string;
    timezone: string;
    recipientUserIds: string[];
  }) =>
    request<ClinicalFinanceReportScheduleDto>('/clinical-finance/report-schedules', {
      method: 'POST',
      body,
    }),
};

export const carePackagesApi = {
  availability: (contactId: string, service: string) =>
    request<CarePackageAvailabilityDto[]>('/care-packages/availability', {
      query: { contactId, service },
    }),
  workspace: () => request<CarePackageWorkspaceDto>('/care-packages'),
  accounts: () => request<Array<{ id: string; name: string }>>('/care-packages/accounts'),
  create: (body: {
    operationId: string;
    contactId: string;
    service: string;
    purchasedQuantity: number;
    totalCents: number;
    validityDays: number;
    accountId: string;
    receivedAt: string;
    evidenceReference: string;
    confirmed: true;
  }) => request<CarePackageDto>('/care-packages', { method: 'POST', body }),
  importHistorical: (body: {
    operationId: string;
    contactId: string;
    service: string;
    purchasedQuantity: number;
    usedQuantity: number;
    totalCents: number;
    purchasedAt: string;
    validUntil: string;
    occurredAt: string;
    reason: string;
    confirmed: true;
  }) => request<CarePackageDto>('/care-packages/historical-import', { method: 'POST', body }),
  requestRefund: (id: string, operationId: string, reason: string) =>
    request<CarePackageDto>(`/care-packages/${id}/refund-request`, {
      method: 'POST',
      body: { operationId, reason, confirmed: true },
    }),
  confirmRefund: (id: string, body: { operationId: string; accountId: string; evidence: string }) =>
    request<CarePackageDto>(`/care-packages/${id}/refund-confirm`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  extendValidity: (id: string, operationId: string, newValidUntil: string, reason: string) =>
    request<CarePackageDto>(`/care-packages/${id}/extend-validity`, {
      method: 'POST',
      body: { operationId, newValidUntil, reason, confirmed: true },
    }),
  closeBalance: (
    id: string,
    body: {
      operationId: string;
      reason: string;
      treatment: 'retain_liability' | 'write_off_approved';
      evidence: string;
    },
  ) =>
    request<CarePackageDto>(`/care-packages/${id}/close-balance`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  setReplacementPolicy: (body: {
    operationId: string;
    minNoticeHours: number;
    creditValidityDays: number;
  }) =>
    request<ReplacementPolicyDto>('/care-packages/replacement-policy', {
      method: 'PUT',
      body: { ...body, confirmed: true },
    }),
  requestReplacement: (body: { operationId: string; contactId: string; service?: string }) =>
    request<{ waitlistEntryId: string; service: string }>('/care-packages/replacement-requests', {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  recordAbsence: (body: { operationId: string; appointmentId: string }) =>
    request<JustifiedAbsenceResultDto>('/care-packages/absences', {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
};

export const clinicalMaterialsApi = {
  workspace: () => request<ClinicalMaterialWorkspaceDto>('/clinical-materials'),
  createMaterial: (body: {
    name: string;
    baseUnit: 'unit' | 'ml' | 'g';
    minimumQuantity: number;
  }) => request<{ id: string }>('/clinical-materials/materials', { method: 'POST', body }),
  accounts: () =>
    request<Array<{ id: string; name: string }>>('/clinical-materials/purchase-accounts'),
  createPurchase: (body: {
    operationId: string;
    materialId: string;
    supplierName: string;
    orderReference: string;
    orderedQuantity: number;
    unitCostCents: number;
    orderedAt: string;
    expectedAt: string;
  }) =>
    request<ClinicalMaterialPurchaseDto>('/clinical-materials/purchases', {
      method: 'POST',
      body,
    }),
  receivePurchase: (
    id: string,
    body: {
      operationId: string;
      quantity: number;
      lotNumber: string;
      location: string;
      expiresAt: string;
      receivedAt: string;
      evidenceReference: string;
      accountId: string;
      dueDate: string;
    },
  ) =>
    request<ClinicalMaterialPurchaseDto>(`/clinical-materials/purchases/${id}/receipts`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  receiveLot: (body: {
    materialId: string;
    lotNumber: string;
    location: string;
    quantity?: number;
    unitCostCents?: number;
    sourceQuantity?: number;
    sourceUnit?: string;
    baseUnitsPerSourceUnit?: number;
    sourceUnitCostCents?: number;
    expiresAt: string;
    receiptReference: string;
  }) => request<ClinicalMaterialLotDto>('/clinical-materials/lots', { method: 'POST', body }),
  changeLotStatus: (id: string, status: 'available' | 'quarantine' | 'recalled', reason: string) =>
    request<ClinicalMaterialLotDto>(`/clinical-materials/lots/${id}/status`, {
      method: 'POST',
      body: { status, reason },
    }),
  consume: (body: {
    operationId: string;
    appointmentId: string;
    materialId: string;
    quantity: number;
    reason: string;
  }) =>
    request<ClinicalMaterialConsumptionDto>('/clinical-materials/consumptions', {
      method: 'POST',
      body,
    }),
  requestSeparation: (body: {
    operationId: string;
    materialId: string;
    quantity: number;
    reason: string;
    ownerName: string;
  }) =>
    request<ClinicalMaterialSeparationResultDto>('/clinical-materials/separation-requests', {
      method: 'POST',
      body,
    }),
  resolveReplenishment: (operationId: string, resolution: string) =>
    request<ClinicalMaterialReplenishmentRequestDto>(
      `/clinical-materials/replenishment-requests/${operationId}/resolve`,
      { method: 'POST', body: { resolution, confirmed: true } },
    ),
};

export type ContactMergePreview = {
  source: Record<string, unknown> & { id: string; name: string };
  target: Record<string, unknown> & { id: string; name: string };
  conflicts: string[];
  referenceCounts: Record<string, number>;
  totalReferences: number;
  protections: string[];
  digest: string;
};

export type ContactMergeResult = {
  contactId: string;
  archivedContactId: string;
  moved: Record<string, number>;
  digest: string;
};

export const pipelinesApi = {
  list: () => request<PipelineDto[]>('/pipelines'),
  /**
   * R074 - a ordem e os nomes vão JUNTOS, e a lista precisa estar completa.
   *
   * Uma chamada por etapa deixaria o funil em estados intermediários: se a
   * segunda falhasse, duas etapas ficariam na mesma posição e a tela desenharia
   * a ordem errada sem ninguém saber.
   */
  setStages: (pipelineId: string, stages: { id: string; name: string }[]) =>
    request<PipelineDto[]>(`/pipelines/${pipelineId}/stages`, { method: 'PUT', body: { stages } }),
};

export const opportunitiesApi = {
  /** RIM-157 - editar titulo, valor e proximo passo sem fechar. */
  update: (
    id: string,
    body: {
      title?: string;
      estimatedValue?: number | null;
      nextStep?: string | null;
      quoteId?: string | null;
      source?: OpportunityDto['source'];
      externalReferrerName?: string | null;
    },
  ) => request<OpportunityDto>(`/opportunities/${id}`, { method: 'PATCH', body }),
  list: (
    query: {
      withoutNextStep?: string;
      search?: string;
      sort?: 'recent' | 'oldest' | 'value_desc';
      page?: number;
      opportunityId?: string;
      pipelineId?: string;
      closedFrom?: string;
      closedTo?: string;
      channel?: string;
      status?: string;
      stageKey?: string;
      contactId?: string;
      limit?: number;
    } = {},
  ) => request<Paginated<OpportunityDto>>('/opportunities', { query }),
  moveStage: (id: string, stageKey: string) =>
    request<OpportunityDto>(`/opportunities/${id}/stage`, {
      method: 'PATCH',
      body: { stageKey },
    }),
};

export const conversationsApi = {
  list: (query: { search?: string; limit?: number; needsAttention?: boolean } = {}) =>
    request<Paginated<ConversationDto>>('/conversations', { query }),
  get: (id: string) => request<ConversationDto>(`/conversations/${id}`),
  messages: (id: string, query: { limit?: number } = {}) =>
    request<Paginated<MessageDto>>(`/conversations/${id}/messages`, { query }),
  handoffContext: (id: string) =>
    request<HandoffContextDto>(`/conversations/${id}/handoff-context`),
  markRead: (id: string) =>
    request<ConversationDto>(`/conversations/${id}/read`, { method: 'PATCH' }),
  send: (id: string, text: string) =>
    request<MessageDto>(`/conversations/${id}/messages`, { method: 'POST', body: { text } }),
  takeover: (id: string) =>
    request<ConversationDto>(`/conversations/${id}/takeover`, { method: 'POST', body: {} }),
  release: (id: string) =>
    request<ConversationDto>(`/conversations/${id}/release`, { method: 'POST', body: {} }),
  pause: (id: string) =>
    request<ConversationDto>(`/conversations/${id}/pause`, { method: 'POST', body: {} }),
};

export const activitiesApi = {
  list: (
    query: {
      contactId?: string;
      search?: string;
      sort?: 'recent' | 'oldest' | 'value_desc';
      page?: number;
      opportunityId?: string;
      limit?: number;
      /** RIM-181 - "agent,system" para o painel "Rimati trabalhando". */
      actorTypes?: string;
    } = {},
  ) => request<Paginated<ActivityDto>>('/activities', { query }),
};

export const agentsApi = {
  list: () => request<AgentDto[]>('/agents'),
  /** RIM-188 - o que a assistente fez hoje. */
  today: () => request<AgentTodayDto>('/agents/today'),
  update: (id: string, body: UpdateAgentRequest) =>
    request<AgentDto>(`/agents/${id}`, { method: 'PATCH', body }),
  setTools: (id: string, enabledTools: ToolName[]) =>
    request<AgentDto>(`/agents/${id}/tools`, { method: 'PUT', body: { enabledTools } }),
  playground: (id: string, body: RunPlaygroundRequest) =>
    request<PlaygroundResultDto>(`/agents/${id}/playground`, { method: 'POST', body }),
};

export interface ConfirmationSettings {
  autoReleaseEnabled: boolean;
  deadlineHours: number;
  offerMinutes: number;
}
export interface ConfirmationRequestRow {
  appointmentId: string;
  startAt: string;
  status: 'awaiting' | 'confirmed' | 'released' | 'closed';
  deadlineAt: string;
  confirmedAt: string | null;
  confirmedVia: 'channel' | 'link' | 'staff' | null;
  releasedAt: string | null;
  offerState: 'none' | 'pending' | 'offered' | 'no-candidate';
  offerWaitlistEntryId: string | null;
}
/** ML141a - fila de chegada sem horário marcado. */
export interface ArrivalQueueRow {
  id: string;
  code: string;
  status: 'waiting' | 'called' | 'served' | 'abandoned';
  position: number | null;
  estimatedWaitMinutes: number | null;
  customerName: string | null;
  plate: string | null;
  phone: string | null;
  services: string[];
  durationMinutes: number;
  source: 'public' | 'reception';
  createdAt: string;
  calledAt: string | null;
  /** ML141b - aviso "sua vez está chegando". */
  turnNotice: {
    state: 'sending' | 'sent' | 'failed' | 'no-conversation' | 'opted-out';
    at: string | null;
    note: string | null;
  } | null;
}
export interface ArrivalNoticeSettings {
  enabled: boolean;
  peopleAhead: number;
}
export interface ArrivalQueueState {
  entries: ArrivalQueueRow[];
  waitingCount: number;
  calledCount: number;
  totalWaitingMinutes: number;
}
export interface ArrivalQueueJoinBody {
  customerName?: string;
  phone?: string;
  plate?: string;
  serviceIds: string[];
  operationId?: string;
  ownTicketKey?: string;
}
export interface ArrivalQueueTicket {
  code: string;
  status: 'waiting' | 'called' | 'served' | 'abandoned';
  position: number | null;
  estimatedWaitMinutes: number | null;
}

export const appointmentsApi = {
  reminders: (date?: string) =>
    request<{ date: string; timeZone: string; items: WhatsAppReminderDto[]; withoutLink: number }>(
      '/appointments/reminders',
      { query: date ? { date } : {} },
    ),
  /** L4 - link da videochamada da consulta (nulo limpa). */
  setVideoCall: (id: string, url: string | null) =>
    request<AppointmentDto>(`/appointments/${id}/video-call`, { method: 'PUT', body: { url } }),
  markReminderOpened: (id: string) =>
    request<{ id: string; openedAt: string }>(`/appointments/${id}/reminder-opened`, {
      method: 'POST',
      body: {},
    }),
  arrivalNoticeSettings: () =>
    request<ArrivalNoticeSettings>('/appointments/arrival-queue/notice-settings'),
  updateArrivalNoticeSettings: (body: Partial<ArrivalNoticeSettings>) =>
    request<ArrivalNoticeSettings>('/appointments/arrival-queue/notice-settings', {
      method: 'PUT',
      body,
    }),
  arrivalQueue: () => request<ArrivalQueueState>('/appointments/arrival-queue'),
  joinArrivalQueue: (body: ArrivalQueueJoinBody & { operationId: string }) =>
    request<ArrivalQueueTicket>('/appointments/arrival-queue', { method: 'POST', body }),
  callNextArrival: () =>
    request<ArrivalQueueRow>('/appointments/arrival-queue/call-next', { method: 'POST', body: {} }),
  callArrival: (id: string) =>
    request<ArrivalQueueRow>(`/appointments/arrival-queue/${id}/call`, { method: 'POST', body: {} }),
  serveArrival: (id: string) =>
    request<ArrivalQueueRow>(`/appointments/arrival-queue/${id}/serve`, { method: 'POST', body: {} }),
  abandonArrival: (id: string, reason?: string) =>
    request<ArrivalQueueRow>(`/appointments/arrival-queue/${id}/abandon`, {
      method: 'POST',
      body: reason ? { reason } : {},
    }),
  list: (
    query: {
      appointmentId?: string;
      from?: string;
      to?: string;
      status?: string;
      contactId?: string;
      limit?: number;
    } = {},
  ) => request<Paginated<AppointmentDto>>('/appointments', { query }),
  availability: (
    query: {
      date?: string;
      period?: string;
      limit?: number;
      service?: string;
      professionalId?: string;
      resourceIds?: string;
    } = {},
  ) => request<AvailabilitySlotDto[]>('/appointments/availability', { query }),
  /** RIM-187 - o resumo do topo da Agenda para o periodo visivel. */
  summary: (query: { from: string; to: string }) =>
    request<CalendarSummaryDto>('/appointments/summary', { query }),
  /** ML138a - faltas por cliente (leitura). */
  noShowsByClient: (query: { from: string; to: string }) =>
    request<NoShowsByClientDto>('/appointments/no-shows-by-client', { query }),
  /** ML138b - confirmação ativa: política, estado por agendamento e liberação. */
  confirmationSettings: () =>
    request<ConfirmationSettings>('/appointments/confirmation/settings'),
  updateConfirmationSettings: (body: Partial<ConfirmationSettings>) =>
    request<ConfirmationSettings>('/appointments/confirmation/settings', { method: 'PUT', body }),
  confirmationRequests: (query: { from: string; to: string }) =>
    request<ConfirmationRequestRow[]>('/appointments/confirmation/requests', { query }),
  dayReview: (date: string) =>
    request<ClinicalDayReviewDto>('/appointments/day-review', { query: { date } }),
  confirmDayReview: (body: {
    date: string;
    acceptPending: boolean;
    note?: string;
    operationId?: string;
  }) =>
    request<ClinicalDayClosingDto>('/appointments/day-review/confirm', {
      method: 'POST',
      body: { ...body, operationId: body.operationId ?? crypto.randomUUID(), confirmed: true },
    }),
  reception: (date: string) =>
    request<ReceptionEntryDto[]>('/appointments/reception', { query: { date } }),
  changeReceptionQueue: (id: string, action: 'wait' | 'call' | 'start') =>
    request<ReceptionEntryDto>(`/appointments/reception/${id}/queue`, {
      method: 'POST',
      body: { action },
    }),
  setReceptionPriority: (id: string, priority: 'standard' | 'priority', reason?: string) =>
    request<{ updated: true }>(`/appointments/reception/${id}/priority`, {
      method: 'POST',
      body: { priority, ...(reason ? { reason } : {}), confirmed: true },
    }),
  issueReceptionBoard: () =>
    request<{ token: string; expiresAt: string }>('/appointments/reception/board-link', {
      method: 'POST',
      body: { confirmed: true },
    }),
  telehealthConfiguration: () =>
    request<TelehealthConfigurationDto>('/appointments/telehealth-configuration'),
  saveTelehealthConfiguration: (body: {
    enabled: boolean;
    platformName?: string;
    supportContact?: string;
    privacyReview?: string;
    emergencyProtocol?: string;
  }) =>
    request<TelehealthConfigurationDto>('/appointments/telehealth-configuration', {
      method: 'PUT',
      body: { ...body, confirmed: true },
    }),
  calendarConnections: () => request<CalendarConnectionDto[]>('/appointments/calendar-connections'),
  createCalendarConnection: (body: {
    provider: 'google' | 'microsoft' | 'other';
    calendarLabel: string;
    professionalId: string;
    syncInbound: boolean;
    syncOutbound: boolean;
    environment: 'sandbox' | 'production';
    homologationEvidence?: string;
  }) =>
    request<CalendarConnectionDto>('/appointments/calendar-connections', {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  importExternalCalendarEvent: (
    id: string,
    body: {
      providerEventId: string;
      providerRevision: number;
      startAt: string;
      endAt: string;
      status: 'active' | 'cancelled';
    },
  ) =>
    request<ExternalCalendarEventDto>(`/appointments/calendar-connections/${id}/events/import`, {
      method: 'POST',
      body,
    }),
  exportCalendarEvents: (id: string, query: { from: string; to: string }) =>
    request<CalendarExportEventDto[]>(`/appointments/calendar-connections/${id}/events/export`, {
      query,
    }),
  professionalPayoutRules: () =>
    request<ProfessionalPayoutRuleDto[]>('/appointments/professional-payouts/rules'),
  createProfessionalPayoutRule: (body: {
    professionalId: string;
    serviceName?: string;
    percentageBps: number;
    releaseTrigger: 'completion' | 'receipt';
    validFrom: string;
    validUntil?: string;
  }) =>
    request<ProfessionalPayoutRuleDto>('/appointments/professional-payouts/rules', {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  professionalPayoutAccruals: () =>
    request<ProfessionalPayoutAccrualDto[]>('/appointments/professional-payouts/accruals'),
  recordProfessionalPayoutReceipt: (
    appointmentId: string,
    receivedCents: number,
    operationId = crypto.randomUUID(),
  ) =>
    request<ProfessionalPayoutAccrualDto>(
      `/appointments/professional-payouts/${appointmentId}/receipt`,
      { method: 'POST', body: { operationId, receivedCents, confirmed: true } },
    ),
  recordProfessionalPayoutPayment: (
    appointmentId: string,
    amountCents: number,
    externalReference: string,
    operationId = crypto.randomUUID(),
  ) =>
    request<ProfessionalPayoutAccrualDto>(
      `/appointments/professional-payouts/${appointmentId}/payment`,
      { method: 'POST', body: { operationId, amountCents, externalReference, confirmed: true } },
    ),
  create: (body: CreateAppointmentRequest) =>
    request<AppointmentDto>('/appointments', {
      method: 'POST',
      body,
    }),
  linkHealthAuthorization: (id: string, authorizationId: string) =>
    request<AppointmentDto>(`/appointments/${id}/health-authorization`, {
      method: 'POST',
      body: { authorizationId, confirmed: true },
    }),
  authorizeFreeReturn: (
    id: string,
    body: { operationId: string; reason: string; confirmed: true },
  ) =>
    request<AppointmentDto>(`/appointments/${id}/free-return-authorization`, {
      method: 'POST',
      body,
    }),
  previewSeries: (body: CreateAppointmentSeriesRequest) =>
    request<AppointmentSeriesPreviewDto>('/appointments/series/preview', {
      method: 'POST',
      body,
    }),
  createSeries: (body: CreateAppointmentSeriesRequest) =>
    request<AppointmentSeriesDto>('/appointments/series', {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  listSeries: () => request<AppointmentSeriesDto[]>('/appointments/series'),
  changeSeriesState: (
    id: string,
    body: { operationId: string; action: 'pause' | 'resume'; reason: string },
  ) =>
    request<AppointmentSeriesDto>(`/appointments/series/${id}/state`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  previewSeriesCancellation: (id: string) =>
    request<AppointmentSeriesChangePreviewDto>(`/appointments/series/${id}/cancel-preview`),
  cancelFutureSeries: (id: string, body: { operationId: string; reason: string }) =>
    request<AppointmentSeriesDto>(`/appointments/series/${id}/cancel-future`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  previewSeriesShift: (id: string, days: number) =>
    request<AppointmentSeriesShiftPreviewDto>(`/appointments/series/${id}/shift-preview`, {
      method: 'POST',
      body: { days },
    }),
  shiftFutureSeries: (id: string, body: { operationId: string; days: number; reason: string }) =>
    request<AppointmentSeriesShiftResultDto>(`/appointments/series/${id}/shift-future`, {
      method: 'POST',
      body: { ...body, confirmed: true },
    }),
  cancel: (id: string, reason?: string) =>
    request<AppointmentDto>(`/appointments/${id}/cancel`, {
      method: 'POST',
      body: { ...(reason ? { reason } : {}) },
    }),
  issueCheckInLink: (id: string) =>
    request<{ token: string; expiresAt: string }>(`/appointments/${id}/check-in-link`, {
      method: 'POST',
    }),
  progress: (id: string, action: 'confirm' | 'arrive' | 'late') =>
    request<AppointmentDto>(`/appointments/${id}/progress`, {
      method: 'POST',
      body: { action },
    }),
  waitlist: () => request<WaitlistEntryDto[]>('/appointments/waitlist'),
  addToWaitlist: (body: CreateWaitlistEntryRequest) =>
    request<WaitlistEntryDto>('/appointments/waitlist', { method: 'POST', body }),
  offerWaitlistSlot: (id: string, startAt: string, expiresMinutes = 30) =>
    request<WaitlistEntryDto>(`/appointments/waitlist/${id}/offer`, {
      method: 'POST',
      body: { startAt, expiresMinutes },
    }),
  acceptWaitlistOffer: (id: string) =>
    request<WaitlistEntryDto>(`/appointments/waitlist/${id}/accept`, {
      method: 'POST',
      body: { confirmed: true },
    }),
  removeFromWaitlist: (id: string, reason: string) =>
    request<WaitlistEntryDto>(`/appointments/waitlist/${id}/remove`, {
      method: 'POST',
      body: { reason },
    }),
};

export const publicQueueApi = {
  services: (token: string) =>
    request<Array<{ id: string; name: string; durationMinutes: number }>>(
      `/check-in/queue/${token}/services`,
    ),
  join: (token: string, body: ArrivalQueueJoinBody) =>
    request<ArrivalQueueTicket & { ticketKey: string }>(`/check-in/queue/${token}`, {
      method: 'POST',
      body,
    }),
  ticket: (token: string, ticketKey: string) =>
    request<ArrivalQueueTicket>(`/check-in/queue/${token}/ticket/${ticketKey}`),
};

/** L4 - recibos de pagamento recebido. */
export type PublicReceiptDto = Omit<ReceiptDto, 'source' | 'link' | 'createdByName' | 'id'>;
export const recibosApi = {
  settings: () => request<ReceiptSettingsDto>('/recibos/ajustes'),
  saveSettings: (body: ReceiptIssuerDto) =>
    request<ReceiptSettingsDto>('/recibos/ajustes', { method: 'PUT', body }),
  listForSource: (kind: ReceiptSourceKind, id: string) =>
    request<ReceiptDto[]>('/recibos', { query: { kind, id } }),
  issue: (body: {
    source: { kind: ReceiptSourceKind; id: string };
    variant?: ReceiptVariant;
    payerDocument?: string;
    description?: string;
    professionalCouncil?: string;
    professionalRegistration?: string;
    expiresInDays?: number;
  }) => request<ReceiptIssueResultDto>('/recibos', { method: 'POST', body }),
  pdf: (id: string) =>
    request<{ fileName: string; mimeType: string; base64: string }>(`/recibos/${id}/pdf`),
  revoke: (id: string) =>
    request<ReceiptDto>(`/recibos/${id}/revogar`, { method: 'POST', body: {} }),
  relink: (id: string, expiresInDays?: number) =>
    request<ReceiptIssueResultDto>(`/recibos/${id}/novo-link`, {
      method: 'POST',
      body: expiresInDays ? { expiresInDays } : {},
    }),
  /** Recibo para reembolso de várias sessões: período (from/to) ou pacote. */
  sessionsPreview: (query: Record<string, string>) =>
    request<ReceiptSessionsPreviewDto>('/recibos/sessoes/previa', { query }),
  issueSessions: (body: Record<string, unknown>) =>
    request<ReceiptIssueResultDto>('/recibos/sessoes', { method: 'POST', body }),
  publicRead: (token: string) =>
    request<PublicReceiptDto>(`/recibos-publicos/${encodeURIComponent(token)}`, {
      anonymous: true,
    }),
  publicPdfUrl: (token: string) => `${API_BASE}/recibos-publicos/${encodeURIComponent(token)}/pdf`,
};

/** C25 - link de aprovação do orçamento (encomenda orçada). */
export const orcamentoLinkApi = {
  status: (orderId: string) =>
    request<QuoteLinkDto | null>(`/orcamento-links/${encodeURIComponent(orderId)}`),
  issue: (orderId: string) =>
    request<QuoteLinkIssueResultDto>(`/orcamento-links/${encodeURIComponent(orderId)}`, {
      method: 'POST',
      body: {},
    }),
  /** C25b: revoga o link; o enviado deixa de abrir. */
  revoke: (orderId: string) =>
    request<{ orderId: string; revoked: boolean }>(`/orcamento-links/${encodeURIComponent(orderId)}`, {
      method: 'DELETE',
    }),
  publicRead: (token: string) =>
    request<PublicQuoteDto>(`/orcamentos-publicos/${encodeURIComponent(token)}`, {
      anonymous: true,
    }),
  publicDecide: (token: string, body: QuoteDecisionInput) =>
    request<PublicQuoteDto>(`/orcamentos-publicos/${encodeURIComponent(token)}/resposta`, {
      method: 'POST',
      body,
      anonymous: true,
    }),
};

export const publicCheckInApi = {
  confirm: (token: string) =>
    request<{
      registered: true;
      arrivalStatus: 'arrived' | 'late';
      arrivalDelayMinutes: number | null;
      arrivedAt: string | null;
      queueCode: string | null;
    }>('/check-in', { method: 'POST', body: { token } }),
  board: (token: string) =>
    request<{ calls: Array<{ code: string; calledAt: string }>; updatedAt: string }>(
      `/check-in/board/${token}`,
    ),
};

export const followUpsApi = {
  list: (query: { status?: string; contactId?: string; limit?: number } = {}) =>
    request<Paginated<FollowUpDto>>('/followups', { query }),
  cancel: (id: string, reason?: string) =>
    request<FollowUpDto>(`/followups/${id}/cancel`, {
      method: 'POST',
      body: { ...(reason ? { reason } : {}) },
    }),
  sendNow: (id: string) => request<FollowUpDto>(`/followups/${id}/send-now`, { method: 'POST' }),
};

export const automationsApi = {
  /** RIM-189 - o que cada automacao produziu. */
  results: () => request<AutomationResultDto[]>('/automations/results'),
  list: () => request<AutomationDto[]>('/automations'),
  update: (
    template: AutomationTemplate,
    body: { isActive?: boolean; steps?: { delayHours: number; message: string }[] },
  ) => request<AutomationDto>(`/automations/${template}`, { method: 'PATCH', body }),
};

/** EPIC 17 e 18 - o Inicio e a tela de Resultados leem daqui, e so daqui. */
export const analyticsApi = {
  dashboard: (period: AnalyticsPeriod) =>
    request<DashboardDto>('/dashboard', { query: { period } }),
  insights: (period: AnalyticsPeriod) => request<InsightsDto>('/insights', { query: { period } }),
  media: (
    period: AnalyticsPeriod,
    kind: MediaAnalyticsKind = 'all',
    state: MediaAnalyticsState = 'all',
  ) => request<MediaAnalyticsDto>('/dashboard/media', { query: { period, kind, state } }),
  mediaExport: (
    period: AnalyticsPeriod,
    kind: MediaAnalyticsKind,
    state: MediaAnalyticsState,
    scope: 'summary' | 'detail',
  ) =>
    request<MediaAnalyticsExportDto>('/dashboard/media/export', {
      query: { period, kind, state, scope },
    }),
};

export const mediaGovernanceApi = {
  get: () => request<MediaGovernanceDto>('/media/governance'),
  requestDeletion: (id: string, reason: string) =>
    request<{ id: string; deletionState: string }>(`/media/${id}/deletion-request`, {
      method: 'POST',
      body: { reason },
    }),
  reviewDeletion: (id: string, decision: 'approve' | 'deny', reason: string) =>
    request<{ id: string; deletionState: string }>(`/media/${id}/deletion-review`, {
      method: 'POST',
      body: { decision, reason },
    }),
};

export const dataGovernanceApi = {
  get: () => request<DataGovernancePolicyDto>('/data-governance'),
  update: (body: UpdateDataGovernancePolicyRequest) =>
    request<DataGovernancePolicyDto>('/data-governance', { method: 'PUT', body }),
  approve: (revision: number) =>
    request<DataGovernancePolicyDto>('/data-governance/approve', {
      method: 'POST',
      body: { revision },
    }),
};

/**
 * ASSISTENTES WOW - a conversa que ensina a assistente.
 *
 * Rota propria, separada de `/conversations`: o que passa aqui nao e atendimento
 * e nao pode compartilhar caminho com quem fala com cliente.
 */
/**
 * CONFIGURAR COM RIMATI - as duas conversas administrativas, uma familia so.
 *
 * `agent-setup` e a conversa com a Livia; `config-setup` e a configuracao da
 * empresa. Mesmo contrato, mesma engine no servidor — o que muda e a base da
 * rota. Dois clientes quase iguais divergiriam na primeira correcao.
 */
function setupApi(base: 'agent-setup' | 'config-setup') {
  return {
    session: () => request<SetupSessionDto>(`/${base}`),
    send: (text: string) =>
      request<{ messages: SetupMessageDto[]; proposals: AgentProposalDto[] }>(`/${base}/messages`, {
        method: 'POST',
        body: { text },
      }),
    decide: (id: string, body: DecideProposalRequest) =>
      request<AgentProposalDto>(`/${base}/proposals/${id}`, { method: 'POST', body }),
  };
}

/**
 * CONVERSA CENTRAL - a gestao por conversa.
 *
 * Rota propria, e nao um terceiro escopo de `setupApi`: o contrato e outro —
 * varias conversas por pessoa, com titulo, recorte e cartoes. Forcar as duas
 * formas num cliente so faria cada chamada carregar campos que a outra ignora.
 */
export const rimatiChatApi = {
  list: () => request<AdminChatSessionDto[]>('/rimati-chat'),
  create: (
    body: {
      contextKind?: AdminContextKind;
      contextId?: string;
      period?: AdminPeriod;
      continueFromSessionId?: string;
    } = {},
  ) => request<AdminChatSessionDto>('/rimati-chat', { method: 'POST', body }),
  session: (id: string) => request<AdminChatViewDto>(`/rimati-chat/${id}`),
  send: (
    id: string,
    body: {
      text: string;
      period?: AdminPeriod;
      channel?: string | null;
      clientRequestId?: string;
      /** O que a tela declara; o servidor valida e confere por id e empresa. */
      screenContext?: ScreenContextDto;
    },
    signal?: AbortSignal,
  ) => request<AdminChatReplyDto>(`/rimati-chat/${id}/messages`, { method: 'POST', body, signal }),
  operation: (id: string, operationId: string) =>
    request<{ id: string; state: string; response: AdminChatReplyDto | null } | null>(
      `/rimati-chat/${id}/operations/${operationId}`,
    ),
  rename: (id: string, title: string) =>
    request<AdminChatSessionDto>(`/rimati-chat/${id}`, { method: 'PATCH', body: { title } }),
  archive: (id: string) =>
    request<AdminChatSessionDto>(`/rimati-chat/${id}/archive`, { method: 'POST' }),
  decide: (id: string, body: DecideProposalRequest) =>
    request<AgentProposalDto>(`/rimati-chat/proposals/${id}`, { method: 'POST', body }),
  task: (taskId: string) => request<AdminTaskDto>(`/rimati-chat/tasks/${taskId}`),
  editTask: (taskId: string, body: { remove?: string[]; message?: string }) =>
    request<AdminTaskDto>(`/rimati-chat/tasks/${taskId}`, { method: 'PATCH', body }),
  approveTask: (taskId: string, version: number) =>
    request<AdminTaskDto>(`/rimati-chat/tasks/${taskId}/approve`, {
      method: 'POST',
      body: { version },
    }),
  resumeTask: (taskId: string) =>
    request<AdminTaskDto>(`/rimati-chat/tasks/${taskId}/resume`, { method: 'POST' }),
  cancelTask: (taskId: string) =>
    request<AdminTaskDto>(`/rimati-chat/tasks/${taskId}/cancel`, { method: 'POST' }),
};

export type RimatiChatPurpose = 'management' | 'own-work' | 'knowledge-reader';

/** The private own-work channel never calls management endpoints. */
export const ownWorkChatApi = {
  list: () => request<AdminChatSessionDto[]>('/business-plans/my-work/chat'),
  create: (body: Parameters<typeof rimatiChatApi.create>[0] = {}) =>
    request<AdminChatSessionDto>('/business-plans/my-work/chat', { method: 'POST', body }),
  session: (id: string) => request<AdminChatViewDto>(`/business-plans/my-work/chat/${id}`),
  send: (id: string, body: Parameters<typeof rimatiChatApi.send>[1], signal?: AbortSignal) =>
    request<AdminChatReplyDto>(`/business-plans/my-work/chat/${id}/messages`, {
      method: 'POST',
      body,
      signal,
    }),
  operation: (id: string, operationId: string) =>
    request<{ id: string; state: string; response: AdminChatReplyDto | null } | null>(
      `/business-plans/my-work/chat/${id}/operations/${operationId}`,
    ),
  rename: (id: string, title: string) =>
    request<AdminChatSessionDto>(`/business-plans/my-work/chat/${id}`, {
      method: 'PATCH',
      body: { title },
    }),
  archive: (id: string) =>
    request<AdminChatSessionDto>(`/business-plans/my-work/chat/${id}/archive`, {
      method: 'POST',
      body: {},
    }),
};
export const knowledgeReaderChatApi = {
  list: () => request<AdminChatSessionDto[]>('/knowledge-team/chat'),
  create: (body: Parameters<typeof rimatiChatApi.create>[0] = {}) =>
    request<AdminChatSessionDto>('/knowledge-team/chat', { method: 'POST', body }),
  session: (id: string) => request<AdminChatViewDto>(`/knowledge-team/chat/${id}`),
  send: (id: string, body: Parameters<typeof rimatiChatApi.send>[1], signal?: AbortSignal) =>
    request<AdminChatReplyDto>(`/knowledge-team/chat/${id}/messages`, {
      method: 'POST',
      body,
      signal,
    }),
  operation: (id: string, operationId: string) =>
    request<{ id: string; state: string; response: AdminChatReplyDto | null } | null>(
      `/knowledge-team/chat/${id}/operations/${operationId}`,
    ),
  rename: (id: string, title: string) =>
    request<AdminChatSessionDto>(`/knowledge-team/chat/${id}`, {
      method: 'PATCH',
      body: { title },
    }),
  archive: (id: string) =>
    request<AdminChatSessionDto>(`/knowledge-team/chat/${id}/archive`, {
      method: 'POST',
      body: {},
    }),
};
export const rimatiChatChannel = (purpose?: RimatiChatPurpose | null) =>
  purpose === 'own-work'
    ? ownWorkChatApi
    : purpose === 'knowledge-reader'
      ? knowledgeReaderChatApi
      : rimatiChatApi;

export const agentSetupApi = setupApi('agent-setup');
export const configSetupApi = setupApi('config-setup');

export const priorityApi = {
  feed: () => request<PriorityFeedDto>('/priorities'),
  dismiss: (key: string, fingerprint: string) =>
    request<PriorityFeedDto>('/priorities/dismiss', { method: 'POST', body: { key, fingerprint } }),
};

export const knowledgeApi = {
  list: (
    query: {
      category?: string;
      search?: string;
      audience?: string;
      page?: number;
      limit?: number;
    } = {},
  ) => request<Paginated<KnowledgeItemDto>>('/knowledge', { query }),
};

export const companyApi = {
  update: (body: UpdateCompanyRequest) =>
    request<CompanyDto>('/company', { method: 'PATCH', body }),
};

/** PR11: o que a empresa mantém quando a pessoa sai; nada é apagado ao desativar. */
export interface ExitReportDto {
  usuario: { id: string; nome: string; ativo: boolean };
  aviso: string;
  mantido: { area: string; motivo: string; colecoes: { colecao: string; guarda: string }[] }[];
  deixaDeAparecerEm: string[];
}

export const usersApi = {
  list: () => request<UserDto[]>('/users'),
  create: (body: CreateUserRequest) =>
    request<CreateUserResultDto>('/users', { method: 'POST', body }),
  update: (id: string, body: UpdateUserRequest) =>
    request<UserDto>(`/users/${id}`, { method: 'PATCH', body }),
  changePassword: (body: ChangePasswordRequest) =>
    request<{ changed: boolean }>('/auth/password', { method: 'POST', body }),
  exitReport: (id: string) => request<ExitReportDto>(`/users/${id}/relatorio-saida`),
};

export const diagnosticsApi = {
  get: () => request<DiagnosticsDto>('/diagnostics'),
  health: () => request<CapabilityHealthReportDto>('/diagnostics/health'),
  securityReadiness: () => request<SecurityReadinessReport>('/diagnostics/security-readiness'),
  externalReadiness: () => request<ExternalReadinessReportDto>('/diagnostics/external-readiness'),
  integrationHomologations: () =>
    request<IntegrationHomologationReportDto>('/diagnostics/integration-homologations'),
  updateIntegrationHomologation: (
    kind: IntegrationHomologationKind,
    body: UpdateIntegrationHomologationRequest,
  ) =>
    request<IntegrationHomologationDto>(`/diagnostics/integration-homologations/${kind}`, {
      method: 'PUT',
      body,
    }),
};

/** R109 - o que falta para a clinica comecar. So le. */
export const onboardingApi = {
  get: () => request<OnboardingDto>('/onboarding'),
};

/** R058 - a area do operador. Todas recusam com 404 para quem nao e operador. */
/** R059 - os relatos DESTA clinica, com o andamento de cada um. */
export const suporteApi = {
  meus: () => request<TicketDto[]>('/suporte/tickets'),
};

export const plataformaApi = {
  visao: (dias = 30) => request<VisaoDoOperadorDto>(`/platform/visao?dias=${dias}`),
  /** ML136 - funil de ativação (só operador da plataforma). */
  ativacao: (dias = 30) => request<AtivacaoFunilDto>(`/platform/ativacao?dias=${dias}`),
  /** R060 - a fila do operador, de todas as empresas. */
  incidentes: (dias = 7) => request<IncidenteDto[]>(`/platform/incidentes?dias=${dias}`),
  sugestoes: (dias = 14) => request<SugestoesDto>(`/platform/sugestoes?dias=${dias}`),
  fila: (state?: string) =>
    request<TicketDaFilaDto[]>(`/platform/tickets${state ? `?state=${state}` : ''}`),
};

export const channelsApi = {
  list: () => request<ChannelDto[]>('/channels'),
};

/**
 * R074 - o CATÁLOGO da clínica: o que ela faz e quem faz.
 *
 * Os dois já existiam no servidor e não tinham porta na interface: a dona só
 * conseguia mudar o próprio catálogo por chamada de API. Enquanto isso, o que a
 * assistente oferece ao cliente sai daqui — então "não dá para editar" queria
 * dizer "a clínica não escolhe o que o Rimati oferece".
 */
export const clinicServicesApi = {
  /*
    `todos=1` traz também os desativados. A tela de configuração precisa deles:
    esconder o que foi desativado faria a pessoa procurar um serviço que ainda
    está lá e criar um segundo com o mesmo nome.
  */
  list: (todos = false) =>
    request<ClinicServiceDto[]>('/appointments/services', {
      query: todos ? { todos: '1' } : {},
    }),
  create: (body: {
    name: string;
    code?: string | null;
    description?: string | null;
    durationMinutes: number;
    bufferMinutes?: number;
    priceCents?: number | null;
    specialty?: string | null;
    modalities?: Array<'presencial' | 'online' | 'domiciliar'>;
    weeklyAvailability?: Array<{ weekday: number; startTime: string; endTime: string }>;
    requiredResourceTypes?: string[];
  }) => request<ClinicServiceDto>('/appointments/services', { method: 'POST', body }),
  update: (
    id: string,
    body: Partial<Omit<ClinicServiceDto, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>,
  ) => request<ClinicServiceDto>(`/appointments/services/${id}`, { method: 'PATCH', body }),
};

export const professionalsApi = {
  list: () => request<ProfessionalDto[]>('/appointments/professionals'),
  create: (body: {
    name: string;
    displayName?: string;
    specialty?: string | null;
    registrationType?: string | null;
    registrationNumber?: string | null;
    linkedUserId?: string | null;
    modalities?: Array<'presencial' | 'online' | 'domiciliar'>;
  }) => request<ProfessionalDto>('/appointments/professionals', { method: 'POST', body }),
  update: (
    id: string,
    body: Partial<Omit<ProfessionalDto, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>,
  ) => request<ProfessionalDto>(`/appointments/professionals/${id}`, { method: 'PATCH', body }),
};

export const clinicalResourcesApi = {
  list: (todos = false) =>
    request<ClinicalResourceDto[]>('/appointments/resources', {
      query: todos ? { todos: '1' } : {},
    }),
  create: (body: {
    code: string;
    name: string;
    type: 'room' | 'equipment';
    location?: string | null;
    cleanupBufferMinutes?: number;
    weeklyAvailability?: Array<{ weekday: number; startTime: string; endTime: string }>;
  }) => request<ClinicalResourceDto>('/appointments/resources', { method: 'POST', body }),
  update: (
    id: string,
    body: Partial<Omit<ClinicalResourceDto, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>,
  ) => request<ClinicalResourceDto>(`/appointments/resources/${id}`, { method: 'PATCH', body }),
  impact: (id: string) =>
    request<ClinicalResourceImpactDto>(`/appointments/resources/${id}/impact`),
  setStatus: (
    id: string,
    body: {
      isActive: boolean;
      reason: string;
      ownerName?: string;
      reviewDueAt?: string;
      confirmed: true;
    },
  ) =>
    request<ClinicalResourceImpactDto>(`/appointments/resources/${id}/status`, {
      method: 'POST',
      body,
    }),
  completeTask: (id: string, appointmentId: string, resolution: string) =>
    request<ClinicalResourceImpactDto>(
      `/appointments/resources/${id}/tasks/${appointmentId}/complete`,
      {
        method: 'POST',
        body: { resolution, confirmed: true },
      },
    ),
};

/** Horário de funcionamento e bloqueios (seção 11.1). */
export const availabilityRulesApi = {
  list: () => request<AvailabilityRuleDto[]>('/appointments/rules'),
  setWeekly: (body: {
    weekday: number;
    startTime: string;
    endTime: string;
    slotMinutes: number;
    /** Quantas pessoas a clínica atende ao mesmo tempo neste dia (R112). */
    capacity?: number;
    isActive?: boolean;
  }) => request<AvailabilityRuleDto>('/appointments/rules/weekly', { method: 'PUT', body }),
  createBlock: (body: {
    blockStartAt: string;
    blockEndAt: string;
    reason?: string;
    professionalId?: string;
    resourceId?: string;
  }) => request<AvailabilityRuleDto>('/appointments/rules/blocks', { method: 'POST', body }),
  remove: (id: string) =>
    request<{ removed: boolean }>(`/appointments/rules/${id}`, { method: 'DELETE' }),
  completeBlockTask: (ruleId: string, appointmentId: string, resolution: string) =>
    request<AvailabilityRuleDto>(`/appointments/rules/${ruleId}/tasks/${appointmentId}/complete`, {
      method: 'POST',
      body: { resolution },
    }),
};

export interface SimulateInput {
  phone: string;
  text: string;
  name?: string;
}

export interface DemoResetSummary {
  companyId: string;
  companyName: string;
  ownerEmail: string;
  counts: {
    contacts: number;
    opportunities: number;
    conversations: number;
    messages: number;
    activities: number;
  };
}

export const devApi = {
  simulateMessage: (body: SimulateInput) =>
    request<SimulateMessageResult>('/dev/simulate-message', { method: 'POST', body }),
  reset: () => request<DemoResetSummary>('/dev/reset', { method: 'POST' }),
};

export interface StoreMediaAssetDto {
  id: string;
  name: string;
  alt: string;
  state: 'uploading' | 'processing' | 'ready' | 'failed';
  width: number | null;
  height: number | null;
  message: string;
}
export interface StoreMediaStatusDto {
  storageConnected: boolean;
  usedBytes: number;
  limitBytes: number;
  assetCount: number;
  assetLimit: number;
  message: string;
}
/** Biblioteca de fotos da loja (gestão: dono, administrador e gerente). */
export const storeMediaApi = {
  list: (after?: string) =>
    request<{ items: StoreMediaAssetDto[]; hasMore: boolean }>(
      '/modules/tools/loja.media-list/read',
      {
        method: 'POST',
        body: { limit: 20, ...(after ? { after } : {}) },
      },
    ),
  status: () =>
    request<StoreMediaStatusDto>('/modules/tools/loja.media-status/read', {
      method: 'POST',
      body: {},
    }),
  get: (id: string) =>
    request<StoreMediaAssetDto>('/modules/tools/loja.media-get/read', {
      method: 'POST',
      body: { id },
    }),
  /** Bytes do menor derivado pronto, só para a gestão; a rota exige o token da sessão. */
  preview: async (id: string): Promise<Blob> => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/store/media/${encodeURIComponent(id)}/preview`, {
      credentials: 'include',
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Foto indisponível.');
    return response.blob();
  },
  /** Passo 1 da migração: gera a prévia e o digest (id) que o apply exige. Só owner/admin. */
  migrationPrepare: (productId: string, revision: number) =>
    request<{ id: string; sourceDigest: string; productId: string }>(
      '/modules/tools/loja.media-migration-prepare/read',
      { method: 'POST', body: { productId, revision } },
    ),
  /** Passo 2: aplica com confirmação explícita (prepare + execute da escrita modular). */
  migrationApply: async (id: string, revision: number) => {
    const receipt = await request<{ id: string }>(
      '/modules/tools/loja.media-migration-apply/prepare',
      { method: 'POST', body: { operationId: crypto.randomUUID(), input: { id, revision } } },
    );
    return request<unknown>('/modules/tools/loja.media-migration-apply/execute', {
      method: 'POST',
      body: { operationId: receipt.id, confirmed: true },
    });
  },
  upload: (file: Blob, name: string) => {
    const form = new FormData();
    form.append('operationId', crypto.randomUUID());
    form.append('name', name);
    form.append('file', file, name);
    return request<StoreMediaAssetDto>('/store/media', { method: 'POST', body: form });
  },
};

export interface VozEstadoDto {
  transcricao: boolean;
  sintese: boolean;
  maxSegundos: number;
  /** VZ10: a voz que vale para esta pessoa (separa o cache de áudio por voz). */
  voz?: string;
  /** VZ10: velocidade de reprodução escolhida pela pessoa. */
  velocidade?: number;
}

export interface TranscricaoDto {
  texto: string;
  confianca: number | null;
  duracaoSegundos: number | null;
}

/** Voz no chat. Os erros 413/415/422/502/503 chegam como ApiError com a mensagem curta da API. */
export const vozApi = {
  estado: () => request<VozEstadoDto>('/voz/estado'),
  transcrever: (audio: Blob, duracaoSegundos?: number) => {
    const form = new FormData();
    const extensao = audio.type.includes('mp4') ? 'mp4' : audio.type.includes('ogg') ? 'ogg' : 'webm';
    form.append('audio', audio, `fala.${extensao}`);
    if (duracaoSegundos !== undefined) form.append('duracaoSegundos', String(Math.round(duracaoSegundos)));
    return request<TranscricaoDto>('/voz/transcrever', { method: 'POST', body: form });
  },
  /** Fala de uma resposta do Rimati (audio/mpeg). Erros chegam como ApiError com a mensagem da API. */
  falar: (mensagemId: string, signal?: AbortSignal) =>
    requestBlob('/voz/falar', { method: 'POST', body: { mensagemId }, signal }),
  /** VZ10: voz e velocidade preferidas da própria pessoa. */
  preferencias: () => request<PreferenciasDeVozDto>('/voz/preferencias'),
  salvarPreferencias: (mudanca: AtualizarPreferenciasDeVozDto) =>
    request<PreferenciasDeVozDto>('/voz/preferencias', { method: 'PUT', body: mudanca }),
  /** VZ10: a frase de amostra na voz pedida (audio/mpeg, guardada no servidor). */
  amostra: (voz: string) => requestBlob(`/voz/amostra/${encodeURIComponent(voz)}`),
};
